import axios from 'axios'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useForm } from '@tanstack/react-form'
import classNames from 'classnames'
import { checkIsEmailValid, createFieldValidators } from '../../tools/validation'

const EditUser = ({ initialFormValue, action, onSave, onAdd, admins }) => {
  const role = useSelector(state => state.user.role)

  const form = useForm({
    defaultValues: initialFormValue,

    onSubmit: ({ value }) => {
      if (action === 'edit') {
        onSave(value)
      }

      if (action === 'add') {
        onAdd(value)
      }
    },
  })

  useEffect(() => {
    form.reset(initialFormValue)
  }, [initialFormValue])

  const [account, setAccount] = useState({ copart: [], iaai: [] })
  const [accountBackup, setAccountBackup] = useState({})

  useEffect(() => {
    if (role === 'sadmin') {
      axios(process.env.REACT_APP_BASE_URL + '/api/accounts')
        .then(response => {
          let accounts = response.data.accounts
          setAccount({ copart: [...accounts.copart], iaai: [...accounts.iaai] })
          setAccountBackup({ copart: [...accounts.copart], iaai: [...accounts.iaai] })
        })
        .catch(err => {
          console.log(err)
        })
    } else {
      axios(process.env.REACT_APP_BASE_URL + '/api/admin')
        .then(response => {
          let admin = response.data.data
          setAccount({ copart: [admin.copartAccount], iaai: [admin.iaaiAccount] })
        })
        .catch(err => {
          console.log(err)
        })
    }
  }, [role])

  const onAdminChange = (field, event) => {
    field.handleChange(event.target.value)
    const activeAdmin = admins.filter(a => a.username === event.target.value)
    if (activeAdmin[0].role === 'sadmin') {
      setAccount({ ...accountBackup })
    } else {
      const acccount = {
        copart: [{ buyer: activeAdmin[0].copartAccount }],
        iaai: typeof activeAdmin[0].iaaiAccount === 'string' ? [{ buyer: activeAdmin[0].iaaiAccount }] : [],
      }
      setAccount({ ...acccount })
    }
  }

  return (
    <div className="edit-users-form-container">
      <form.Field
        name="email"
        validators={createFieldValidators(
          value => !value && 'Email is required',
          value => !checkIsEmailValid(value) && 'Invalid email'
        )}
      >
        {field => (
          <div className="form-group">
            <label className="form-label">Email</label>

            <input
              type="email"
              className={classNames('form-control', field.state.meta.errors.length > 0 && 'form-control-error')}
              value={field.state.value}
              onInput={event => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
              placeholder="Enter email"
              required
            />
            {field.state.meta.errors.length > 0 && <span className="form-error">{field.state.meta.errors[0]}</span>}
          </div>
        )}
      </form.Field>

      <form.Field name="username" validators={createFieldValidators(value => !value && 'Username is required')}>
        {field => (
          <div className="form-group">
            <label className="form-label">Username</label>

            <input
              type="text"
              className={classNames('form-control', field.state.meta.errors.length > 0 && 'form-control-error')}
              value={field.state.value}
              onInput={event => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
              placeholder="Enter username"
              required
            />
            {field.state.meta.errors.length > 0 && <span className="form-error">{field.state.meta.errors[0]}</span>}
          </div>
        )}
      </form.Field>

      <form.Field
        name="password"
        validators={createFieldValidators(value => action === 'add' && !value && 'Password is required')}
      >
        {field => (
          <div className="form-group">
            <label className="form-label">Password</label>

            <input
              type="password"
              className={classNames('form-control', field.state.meta.errors.length > 0 && 'form-control-error')}
              value={field.state.value}
              onInput={event => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
              placeholder="Enter password"
              required
            />
            {field.state.meta.errors.length > 0 && <span className="form-error">{field.state.meta.errors[0]}</span>}
          </div>
        )}
      </form.Field>

      {role === 'sadmin' && (
        <form.Field name="adminName">
          {field => (
            <div className="form-group">
              <label className="form-label">Admin name</label>
              <select value={field.state.value} className="select" onChange={event => onAdminChange(field, event)}>
                <option value="">Admin name</option>
                {admins.map(admin => {
                  return (
                    <option key={admin.id} value={admin.username}>
                      {admin.username}
                    </option>
                  )
                })}
              </select>
            </div>
          )}
        </form.Field>
      )}

      <form.Field name="copartAccount">
        {field => (
          <div className="form-group">
            <label className="form-label">Copart account</label>

            <select
              value={field.state.value}
              className={classNames('select', field.state.meta.errors.length > 0 && 'form-control-error')}
              onChange={event => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
            >
              <option value="">Copart account</option>
              {account.copart.map(a => {
                return (
                  <option key={role === 'sadmin' ? a.buyer : a} value={role === 'sadmin' ? a.buyer : a}>
                    {role === 'sadmin' ? a.buyer : a}
                  </option>
                )
              })}
            </select>
            {field.state.meta.errors.length > 0 && <span className="form-error">{field.state.meta.errors[0]}</span>}
          </div>
        )}
      </form.Field>

      <form.Field name="iaaiAccount">
        {field => (
          <div className="form-group">
            <label className="form-label">IAAI account</label>

            <select
              value={field.state.value}
              className={classNames('select', field.state.meta.errors.length > 0 && 'form-control-error')}
              onChange={event => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
            >
              <option value="">IAAI account</option>
              {account.iaai.map(a => {
                return (
                  <option key={role === 'sadmin' ? a.buyer : a} value={role === 'sadmin' ? a.buyer : a}>
                    {role === 'sadmin' ? a.buyer : a}
                  </option>
                )
              })}
            </select>
            {field.state.meta.errors.length > 0 && <span className="form-error">{field.state.meta.errors[0]}</span>}
          </div>
        )}
      </form.Field>

      <form.Field name="country">
        {field => (
          <div className="form-group">
            <label className="form-label">Country</label>

            <input
              className={classNames('form-control', field.state.meta.errors.length > 0 && 'form-control-error')}
              onInput={event => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
              value={field.state.value}
              type="text"
              placeholder="Enter country"
            />
            {field.state.meta.errors.length > 0 && <span className="form-error">{field.state.meta.errors[0]}</span>}
          </div>
        )}
      </form.Field>

      <form.Field name="city">
        {field => (
          <div className="form-group">
            <label className="form-label">City</label>

            <input
              className={classNames('form-control', field.state.meta.errors.length > 0 && 'form-control-error')}
              onInput={event => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
              value={field.state.value}
              type="text"
              placeholder="Enter city"
            />
            {field.state.meta.errors.length > 0 && <span className="form-error">{field.state.meta.errors[0]}</span>}
          </div>
        )}
      </form.Field>

      <form.Field name="name">
        {field => (
          <div className="form-group">
            <label className="form-label">Name</label>

            <input
              className={classNames('form-control', field.state.meta.errors.length > 0 && 'form-control-error')}
              onInput={event => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
              value={field.state.value}
              type="text"
              placeholder="Enter name"
            />
            {field.state.meta.errors.length > 0 && <span className="form-error">{field.state.meta.errors[0]}</span>}
          </div>
        )}
      </form.Field>

      <form.Field name="surname">
        {field => (
          <div className="form-group">
            <label className="form-label">Surname</label>

            <input
              className={classNames('form-control', field.state.meta.errors.length > 0 && 'form-control-error')}
              onInput={event => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
              value={field.state.value}
              type="text"
              placeholder="Enter surname"
            />
            {field.state.meta.errors.length > 0 && <span className="form-error">{field.state.meta.errors[0]}</span>}
          </div>
        )}
      </form.Field>

      <form.Field name="phone">
        {field => (
          <div className="form-group">
            <label className="form-label">Phone</label>

            <input
              className={classNames('form-control', field.state.meta.errors.length > 0 && 'form-control-error')}
              onInput={event => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
              value={field.state.value}
              type="text"
              placeholder="Enter phone"
            />
            {field.state.meta.errors.length > 0 && <span className="form-error">{field.state.meta.errors[0]}</span>}
          </div>
        )}
      </form.Field>

      <form.Field name="bidLimit" validators={createFieldValidators(value => !value && 'Bid limit is required')}>
        {field => (
          <div className="form-group">
            <label className="form-label">Bid limit</label>

            <input
              className={classNames('form-control')}
              onInput={event => field.handleChange(event.target.value)}
              onBlur={field.handleBlur}
              value={field.state.value}
              type="text"
              placeholder="Enter bid limit"
            />
            {field.state.meta.errors.length > 0 && <span className="form-error">{field.state.meta.errors[0]}</span>}
          </div>
        )}
      </form.Field>

      <div className="checkboxes">
        <form.Field name="copartStatus">
          {field => (
            <div className="checkbox-box">
              <label className="checkbox-label" htmlFor="copart-status">
                Copart
              </label>
              <input
                type="checkbox"
                id="copart-status"
                onChange={e => field.handleChange(e.target.checked)}
                checked={field.state.value}
              />
            </div>
          )}
        </form.Field>

        <form.Field name="iaaiStatus">
          {field => (
            <div className="checkbox-box" style={{ marginLeft: '20px' }}>
              <label className="checkbox-label" htmlFor="iaai-status">
                IAAI
              </label>
              <input
                type="checkbox"
                id="iaai-status"
                onChange={e => field.handleChange(e.target.checked)}
                checked={field.state.value}
              />
            </div>
          )}
        </form.Field>

        <form.Field name="prebidStatus">
          {field => (
            <div className="checkbox-box" style={{ marginLeft: '20px' }}>
              <label className="checkbox-label" htmlFor="pre-bid">
                Prebid
              </label>
              <input
                type="checkbox"
                id="pre-bid"
                onChange={e => field.handleChange(e.target.checked)}
                checked={field.state.value}
              />
            </div>
          )}
        </form.Field>

        <form.Field name="liveBidStatus">
          {field => (
            <div className="checkbox-box" style={{ marginLeft: '20px' }}>
              <label className="checkbox-label" htmlFor="live-bid">
                Live bid
              </label>
              <input
                type="checkbox"
                id="live-bid"
                onChange={e => field.handleChange(e.target.checked)}
                checked={field.state.value}
              />
            </div>
          )}
        </form.Field>

        <form.Field name="livePreBid">
          {field => (
            <div className="checkbox-box" style={{ marginLeft: '20px' }}>
              <label className="checkbox-label" htmlFor="live-pre-bid">
                Pre bid in live
              </label>
              <input
                type="checkbox"
                id="live-pre-bid"
                onChange={e => field.handleChange(e.target.checked)}
                checked={field.state.value}
              />
            </div>
          )}
        </form.Field>
      </div>

      <div className="submit-button-container">
        {action === 'edit' && (
          <button className="save-user-button" onClick={form.handleSubmit}>
            Save
          </button>
        )}

        {action === 'add' && (
          <button className="save-user-button" onClick={form.handleSubmit}>
            Add new
          </button>
        )}
      </div>
    </div>
  )
}

export default EditUser
